"use client";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Typography,
} from "@mui/material";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import NoOne from "../../assets/cheer_up.png";
import IndiaFranchise from "../../assets/india-franchise.png";
import Enquire from "./Enquire";
import CheckIcon from "@mui/icons-material/Check";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { ApiService } from "@/app/services/AppService";
import { FranchiseData } from "@/app/classes/main";
import bgImage from "../../assets/franchise.png";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./marketing.css";
import StateAvailable from "./StateAvailable";
import { motion } from "framer-motion";

export const Marketing = () => {
  const [franchiseData, setFranchiseData] = useState<FranchiseData | null>(
    null
  );
  const apiService = useMemo(() => new ApiService(), []);
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 1 } },
  };

  const getFranchiseDetails = useCallback(async () => {
    try {
      const response = await apiService.get<any>(apiService.franchiseDetails);
      setFranchiseData(response?.object);
    } catch (error) {
      console.error("Error fetching Franchise Data:", error);
    }
  }, [apiService]);

  useEffect(() => {
    getFranchiseDetails();
  }, [getFranchiseDetails]);

  const heroGradient =
    "linear-gradient(135deg, #123524 0%, #1f7a4d 45%, #1a6b42 100%)";

  const contentGutter = {
    width: "100%",
    maxWidth: "min(92%, 1220px)",
    marginLeft: "auto",
    marginRight: "auto",
    paddingX: { xs: 2, sm: 3 },
    boxSizing: "border-box" as const,
  };

  return (
    <Box sx={{ pb: { xs: 10, sm: 2 } }}>
      <Box
        sx={{
          background: heroGradient,
          padding: { xs: "1.25rem 0 2rem", sm: "2rem 0 3rem" },
          boxShadow: "0 12px 40px rgba(18, 53, 36, 0.2)",
        }}
      >
        <motion.div initial="hidden" animate="visible" variants={fadeIn}>
          <Box
            sx={{
              ...contentGutter,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              paddingTop: { xs: "0.5rem", sm: "0.5rem" },
              paddingBottom: { xs: "1rem", sm: "1.5rem" },
              gap: 1.5,
            }}
          >
            <Image
              src={NoOne}
              width={500}
              height={500}
              style={{
                height: "auto",
                width: "3.25rem",
                objectFit: "cover",
              }}
              alt=""
            />

            <Typography
              variant="h5"
              component="p"
              sx={{
                color: "rgba(255,255,255,0.95)",
                fontWeight: 700,
                fontSize: { xs: "14px", sm: "18px" },
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              India&#39;s top rated home salon
            </Typography>
          </Box>
        </motion.div>
        <Box
          sx={{
            ...contentGutter,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: { xs: "100%", md: "min(520px, 100%)" },
              textAlign: "center",
              flex: { md: "0 0 auto" },
            }}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              style={{ width: "100%" }}
            >
              <Typography
                variant="h3"
                component="h1"
                sx={{
                  width: "100%",
                  fontWeight: 700,
                  color: "#fff",
                  textAlign: "center",
                  fontSize: {
                    xs: "1.5rem",
                    sm: "1.85rem",
                    md: "2.35rem",
                    lg: "2.75rem",
                  },
                  marginBottom: { xs: 1, sm: 1.25 },
                  textShadow: "0 2px 24px rgba(0,0,0,0.15)",
                }}
                className="animate-character-2"
              >
                Become your own boss
              </Typography>
              <Typography
                component="p"
                sx={{
                  width: "100%",
                  fontWeight: 600,
                  color: "rgba(255,255,255,0.92)",
                  textAlign: "center",
                  fontSize: {
                    xs: "1rem",
                    sm: "1.15rem",
                    md: "1.25rem",
                  },
                  mb: { xs: 2, md: 2.5 },
                  lineHeight: 1.5,
                }}
              >
                Own a SalonTym franchise in your city - we&apos;ll walk you
                through every step.
              </Typography>
              <Box
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "1px solid rgba(255,255,255,0.2)",
                  boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
                  maxWidth: "100%",
                  mx: "auto",
                }}
              >
                <Image
                  height={500}
                  width={500}
                  src={bgImage}
                  alt="SalonTym franchise"
                  style={{
                    objectFit: "cover",
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </motion.div>
          </Box>
          <Box
            sx={{
              width: "100%",
              maxWidth: "420px",
              flex: { md: "0 0 auto" },
              alignSelf: { xs: "center", md: "flex-start" },
            }}
          >
            <motion.div initial="hidden" animate="visible" variants={fadeIn}>
              <Enquire />
            </motion.div>
          </Box>
        </Box>
      </Box>
      <motion.div initial="hidden" animate="visible" variants={fadeIn}>
        <Box
          sx={{
            background:
              "linear-gradient(180deg, rgba(245, 251, 247, 0.98) 0%, #ffffff 100%)",
            py: { xs: 3, sm: 5 },
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              ...contentGutter,
              textAlign: "center",
              justifyContent: "center",
            }}
          >
            <Grid item xs={12}>
              <Typography
                variant="h4"
                component="h2"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3.25rem" },
                  mb: { xs: 2, sm: 3 },
                }}
                className="animate-charcter"
              >
                Earn up to ₹1 crore*
              </Typography>
            </Grid>
            {[
              { name: "Initial Investment", value: "5–6 Lakhs" },
              { name: "Payback Period", value: "12–15 Months" },
              { name: "Gross Margin", value: "60%–70%" },
              { name: "Total Carpet Area", value: "700–1000 Sq. Ft." },
            ].map((item, index) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "16rem",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#123524",
                      background:
                        "linear-gradient(180deg, rgba(223, 246, 232, 0.95) 0%, rgba(246, 251, 248, 0.9) 100%)",
                      padding: "0.65rem 0.75rem",
                      borderRadius: "12px 12px 0 0",
                      border: "1px solid rgba(18, 53, 36, 0.12)",
                      borderBottom: "none",
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.35,
                    }}
                  >
                    {item.name}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "#fff",
                      background:
                        "linear-gradient(135deg, #123524 0%, #1f7a4d 100%)",
                      padding: "0.85rem 0.75rem",
                      borderRadius: "0 0 12px 12px",
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.1rem" },
                      boxShadow: "0 8px 24px rgba(18, 53, 36, 0.2)",
                      textAlign: "center",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </motion.div>

      <Box
        sx={{
          background:
            "linear-gradient(180deg, #ffffff 0%, rgba(245, 251, 247, 0.5) 100%)",
          py: { xs: 2, sm: 4 },
        }}
      >
        <Box
          sx={{
            ...contentGutter,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            justifyContent: "center",
            alignItems: { xs: "center", md: "flex-start" },
            gap: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              flex: { md: "0 0 auto" },
              width: "100%",
              maxWidth: { xs: "100%", md: "min(560px, 100%)" },
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Image
              src={IndiaFranchise}
              width={2000}
              height={2000}
              style={{
                objectFit: "contain",
                width: "100%",
                maxWidth: "560px",
                height: "auto",
              }}
              alt="SalonTym franchise across India"
            />
          </Box>

          <Box
            sx={{
              flex: { md: "0 0 auto" },
              width: "100%",
              maxWidth: "520px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                boxShadow: "0 12px 40px rgba(18, 53, 36, 0.1)",
                borderRadius: "16px",
                padding: { xs: "1.25rem", sm: "1.5rem" },
                margin: "0 auto",
                width: "100%",
                maxWidth: "520px",
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(246,251,248,0.85) 100%)",
                border: "1px solid rgba(18, 53, 36, 0.1)",
                color: "#123524",
              }}
            >
              <Box sx={{ width: "100%", textAlign: "left" }}>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    fontWeight: 700,
                    marginBottom: "0.75rem",
                    color: "#123524",
                    borderBottom: "2px solid rgba(31, 122, 77, 0.25)",
                    pb: 0.5,
                    display: "inline-block",
                    textAlign: "left",
                  }}
                >
                  {franchiseData?.franchiseFacts[0]?.header}
                </Typography>
                {franchiseData?.franchiseFacts[0]?.content?.map(
                  (item: any, index: any) => (
                    <Typography
                      key={index}
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: "#123524",
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.5,
                        mb: 0.75,
                        lineHeight: 1.55,
                      }}
                    >
                      <CheckIcon
                        sx={{
                          color: "#1f7a4d",
                          fontSize: "1.1rem",
                          mt: "2px",
                          flexShrink: 0,
                        }}
                      />
                      <span>{item}</span>
                    </Typography>
                  )
                )}
              </Box>
              {franchiseData?.franchiseResponseSupports &&
                franchiseData.franchiseResponseSupports.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        marginBottom: "0.75rem",
                        color: "#123524",
                        borderBottom: "2px solid rgba(31, 122, 77, 0.25)",
                        pb: 0.5,
                        display: "inline-block",
                        textAlign: "left",
                      }}
                    >
                      Support
                    </Typography>
                    <Box>
                      {franchiseData.franchiseResponseSupports.map(
                        (item, index) => (
                          <Accordion
                            key={index}
                            sx={{
                              background: "transparent",
                              boxShadow: "none",
                              border: "1px solid rgba(18, 53, 36, 0.08)",
                              borderRadius: "12px !important",
                              mb: 1,
                              "&:before": { display: "none" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMoreIcon sx={{ color: "#1f7a4d" }} />
                              }
                              sx={{
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                color: "#123524",
                                minHeight: "48px",
                              }}
                            >
                              <HelpOutlineIcon
                                sx={{ paddingRight: "8px", color: "#1f7a4d" }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600 }}
                              >
                                {item?.heading}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography
                                variant="body2"
                                sx={{ color: "#5e6d63", lineHeight: 1.6 }}
                              >
                                <CheckIcon
                                  sx={{
                                    color: "#1f7a4d",
                                    paddingRight: "6px",
                                    verticalAlign: "middle",
                                    fontSize: "1rem",
                                  }}
                                />
                                {item?.description}
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        )
                      )}
                    </Box>
                  </Box>
                )}
            </Box>
          </Box>
        </Box>
      </Box>
      <StateAvailable />
    </Box>
  );
};
